// Single Responsibility: Create/Edit Goal Modal
import React, { useState, useEffect } from 'react';
import { X, Target, Clock, BarChart3 } from 'lucide-react';
import { apiService } from '../../services/api';
import { Goal } from '../../types';

interface CreateGoalModalProps {
  projectId: string;
  parentGoal?: Goal;
  editGoal?: Goal;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateGoalModal: React.FC<CreateGoalModalProps> = ({
  projectId,
  parentGoal,
  editGoal,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    estimatedHours: '',
    targetDate: '',
    complexityScore: '',
    standalonePotential: false
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isEditing = !!editGoal;

  useEffect(() => {
    if (editGoal) {
      setFormData({
        name: editGoal.name || '',
        description: editGoal.description || '',
        estimatedHours: editGoal.estimatedHours?.toString() || '',
        targetDate: editGoal.targetDate || '',
        complexityScore: editGoal.complexityScore?.toString() || '',
        standalonePotential: editGoal.standalonePotential || false
      });
    }
  }, [editGoal]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Goal name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const goalData = {
        projectId,
        parentGoalId: parentGoal?.id || undefined,
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        estimatedHours: formData.estimatedHours ? parseInt(formData.estimatedHours) : undefined,
        targetDate: formData.targetDate || undefined,
        complexityScore: formData.complexityScore ? parseInt(formData.complexityScore) : undefined,
        standalonePotential: formData.standalonePotential
      };

      if (isEditing && editGoal) {
        await apiService.updateGoal(editGoal.id, goalData);
      } else {
        await apiService.createGoal(goalData);
      }

      onSuccess();
    } catch (err) {
      console.error('Goal operation failed:', err);
      setError(isEditing ? 'Failed to update goal' : 'Failed to create goal');
    } finally {
      setLoading(false);
    }
  };

  const getComplexityLabel = (score: number) => {
    if (score <= 3) return 'Simple';
    if (score <= 6) return 'Moderate';
    if (score <= 8) return 'Complex';
    return 'Very Complex';
  };

  const complexityScore = parseInt(formData.complexityScore) || 1;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <Target className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">
                {isEditing ? 'Edit Goal' : 'Create New Goal'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {parentGoal && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  <strong>Parent Goal:</strong> {parentGoal.name}
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Goal Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input-field"
                placeholder="e.g., Implement user authentication"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-field resize-none"
                placeholder="Describe what needs to be accomplished..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Estimated Hours
                </label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={formData.estimatedHours}
                  onChange={(e) => setFormData({ ...formData, estimatedHours: e.target.value })}
                  className="input-field"
                  placeholder="40"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Date
                </label>
                <input
                  type="date"
                  value={formData.targetDate}
                  onChange={(e) => setFormData({ ...formData, targetDate: e.target.value })}
                  className="input-field"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <BarChart3 className="w-4 h-4 inline mr-1" />
                Complexity: {complexityScore}/10 ({getComplexityLabel(complexityScore)})
              </label>
              <input
                type="range"
                min="1"
                max="10"
                value={formData.complexityScore || '1'}
                onChange={(e) => setFormData({ ...formData, complexityScore: e.target.value })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Simple</span>
                <span>Moderate</span>
                <span>Complex</span>
                <span>Very Complex</span>
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="standalonePotential"
                checked={formData.standalonePotential}
                onChange={(e) => setFormData({ ...formData, standalonePotential: e.target.checked })}
                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <label htmlFor="standalonePotential" className="ml-2 text-sm text-gray-700">
                Has standalone potential (could be separate project)
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex items-center gap-2"
              >
                <Target className="w-4 h-4" />
                {loading ? 
                  (isEditing ? 'Updating...' : 'Creating...') :
                  (isEditing ? 'Update Goal' : 'Create Goal')
                }
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};